import BugReportIcon from "@mui/icons-material/BugReport";
import BuildIcon from "@mui/icons-material/Build";
import CloudQueueIcon from "@mui/icons-material/CloudQueue";
import CodeIcon from "@mui/icons-material/Code";
import HubIcon from "@mui/icons-material/Hub";
import InsightsIcon from "@mui/icons-material/Insights";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import PsychologyIcon from "@mui/icons-material/Psychology";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import StorageIcon from "@mui/icons-material/Storage";
import SmartToyIcon from "@mui/icons-material/SmartToy";

/**
 * Compact skill summary shown on the home page.
 *
 * AI engineering leads. The grid is the first thing on the page that names
 * specific technologies, so its order is the page's argument about what this
 * person does: AI on top of a backend, not a backend with AI mentioned.
 */
export const skillHighlights = [
  {
    title: "LLM Applications",
    Icon: PsychologyIcon,
    tech: [
      "OpenAI API",
      "Prompt Engineering",
      "Tool Calling",
      "Structured Outputs",
      "Streaming",
    ],
  },
  {
    title: "RAG & Retrieval",
    Icon: ManageSearchIcon,
    tech: ["RAG", "Embeddings", "Vector Search", "Chunking", "Reranking"],
  },
  {
    title: "AI Agents",
    Icon: SmartToyIcon,
    tech: ["AI Agents", "LangChain", "LangGraph", "MCP", "n8n"],
  },
  {
    title: "Backend",
    Icon: StorageIcon,
    tech: [
      "Java",
      "Spring Boot",
      "Microservices",
      "REST APIs",
      "Python",
      "FastAPI",
    ],
  },
  {
    title: "Machine Learning",
    Icon: HubIcon,
    tech: [
      "Random Forest",
      "CNNs",
      "scikit-learn",
      "Pandas",
      "Computer Vision",
      "YOLOv8",
    ],
  },
  {
    title: "Platform & Quality",
    Icon: CloudQueueIcon,
    tech: ["PostgreSQL", "pgvector", "Docker", "AWS", "K6", "LLM Evaluation"],
  },
];

/**
 * Full breakdown shown on the about page.
 *
 * One array per group. An earlier version split each group into `tech` and
 * `learning`, rendering the second set in dashed pills under a "Learning"
 * label; Chafic decided these are skills rather than intentions, so the two
 * were folded together and the dashed variant removed.
 *
 * Ordering still carries the old distinction: within each group the items he
 * has shipped with come first, and the rest follow. Keep new entries in that
 * order.
 *
 * Everything here is interview surface. Do not add a technology to this file
 * that he would not want to be questioned on.
 */
export const skillGroups = [
  {
    title: "LLM Applications",
    Icon: PsychologyIcon,
    span: 6,
    tech: [
      "OpenAI API",
      "Prompt Engineering",
      "Few-shot & Chain-of-Thought",
      "Function / Tool Calling",
      "Structured Outputs (JSON)",
      "Streaming Responses",
      "Anthropic Claude API",
      "Google Gemini",
      "Context Engineering",
      "Model Routing & Fallback",
      "Token & Cost Budgeting",
    ],
  },
  {
    title: "RAG & Retrieval",
    Icon: ManageSearchIcon,
    span: 6,
    tech: [
      "RAG",
      "Embeddings",
      "Vector Search",
      "Chunking Strategies",
      "pgvector",
      "Qdrant",
      "Pinecone",
      "Hybrid Search (BM25 + Dense)",
      "Reranking (Cross-Encoders)",
      "Query Rewriting / HyDE",
      "Grounding & Citations",
    ],
  },
  {
    title: "AI Agents & Orchestration",
    Icon: SmartToyIcon,
    span: 6,
    tech: [
      "AI Agents",
      "Tool Calling",
      "n8n",
      "Workflow Automation",
      "LangChain",
      "LangGraph",
      "LlamaIndex",
      "Model Context Protocol (MCP)",
      "Multi-Agent Systems",
      "Agent Memory & State",
      "Human-in-the-Loop",
    ],
  },
  {
    title: "Evaluation & Observability",
    Icon: InsightsIcon,
    span: 6,
    tech: [
      // The umbrella term the home page card uses; the four tools below are
      // what it actually means in practice. Keep the two pages in step.
      "LLM Evaluation",
      "Regression Testing",
      "Golden Datasets",
      "Grafana",
      "Prometheus",
      "LangSmith",
      "Langfuse",
      "RAGAS",
      "DeepEval",
      "LLM-as-a-Judge",
      "Prompt Regression Testing",
      "Tracing & Cost Dashboards",
    ],
  },
  {
    title: "AI in Production",
    Icon: RocketLaunchIcon,
    span: 6,
    // The strongest group on the page: the backend half is work already done
    // at FOO, and it is exactly what makes an LLM application survive.
    tech: [
      "Flask",
      "Docker",
      "AWS Bedrock",
      "Rate Limiting & Retries",
      "Caching",
      "Latency Optimisation",
      "FastAPI",
      "vLLM",
      "Ollama",
      "Semantic Caching",
      "Guardrails",
      "PII Redaction",
      "Prompt Injection Defence",
    ],
  },
  {
    title: "Machine Learning",
    Icon: HubIcon,
    span: 6,
    tech: [
      "Random Forest",
      "Convolutional Neural Networks (CNN)",
      "Feature Engineering",
      "Hyperparameter Tuning",
      "Data Preprocessing",
      "YOLOv8",
      "LBPH",
      "OpenCV",
      "Computer Vision",
      "Model Integration",
      "Pandas",
      "NumPy",
      "PyTorch",
      "scikit-learn",
      "Hugging Face Transformers",
      "Fine-tuning (LoRA / QLoRA)",
      "MLflow",
      "Experiment Tracking",
    ],
  },
  {
    title: "Backend Development",
    Icon: BuildIcon,
    span: 6,
    tech: [
      "Java",
      "Spring Boot",
      "Spring Security",
      "Microservices",
      "REST APIs",
      "Webhooks",
      "Hibernate",
      "JPA",
      "WSO2",
      "Laravel",
    ],
  },
  {
    title: "Data",
    Icon: StorageIcon,
    span: 6,
    tech: [
      "PostgreSQL",
      "MySQL",
      "SQL",
      "Database Modeling",
      "DB Migration",
      "Vector Databases",
      "Data Pipelines",
    ],
  },
  {
    title: "Cloud & DevOps",
    Icon: CloudQueueIcon,
    span: 6,
    tech: [
      "Docker",
      "AWS (EC2, S3, Lambda, IAM)",
      "CI/CD",
      "Git",
      "GitHub Actions",
      "Jira",
      "Kubernetes",
    ],
  },
  {
    title: "Quality Assurance",
    Icon: BugReportIcon,
    span: 6,
    tech: [
      "Unit Testing",
      "Manual Testing",
      "Automation Testing",
      "Pytest",
      "Selenium",
      "K6",
      "Postman",
      "Swagger",
    ],
  },
  {
    title: "Programming",
    Icon: CodeIcon,
    span: 6,
    tech: ["Java", "Python", "C++", "TypeScript", "React.ts", "PHP"],
  },
  {
    title: "Soft Skills",
    Icon: PsychologyIcon,
    span: 12,
    tech: [
      "Problem Solving (LCPC - Lebanese Collegiate Programming Contest)",
      "Attention to Detail",
      "Adaptability",
      "Client Communication",
      "Technical Mentorship",
    ],
  },
];

/**
 * Spoken languages. They were only on the CV, and for a Lebanon-based engineer
 * working with Gulf banking clients they are a professional fact, not trivia.
 */
export const spokenLanguages = [
  { name: "Arabic", level: "Native" },
  { name: "English", level: "Full professional" },
  { name: "French", level: "Intermediate" },
];
